// What every widget can reach without prop drilling: the conversation's saved
// widget states, how to save one, whether the view is read-only, how to ask a
// follow-up (input widgets, email template pills) and the systems by slug.

import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react'

export const WidgetContext = createContext({
  states: {},
  save: null,
  readOnly: true,
  onAsk: null,
  systemsMap: new Map(),
})

export const useWidgetContext = () => useContext(WidgetContext)

/**
 * A stateful widget's saved edits. The local value wins while the user works,
 * so typing never waits on the network; saves are optional-debounced and
 * flushed when the widget unmounts.
 *
 *   const [state, update] = useWidgetState(toolCallId, { done: [] })
 *   update((s) => ({ ...s, done: [...s.done, id] }))
 *   update({ ...state, body }, { debounce: 400 })
 */
export function useWidgetState(toolCallId, fallback) {
  const { states, save, readOnly } = useWidgetContext()
  const stored = states?.[toolCallId]
  const [local, setLocal] = useState(undefined)
  const value = local ?? stored ?? fallback

  const current = useRef(value)
  current.current = value
  const saveRef = useRef(save)
  saveRef.current = save
  const pending = useRef(undefined)
  const timer = useRef(null)

  const flush = useCallback(() => {
    clearTimeout(timer.current)
    if (pending.current !== undefined) {
      saveRef.current?.(toolCallId, pending.current)
      pending.current = undefined
    }
  }, [toolCallId])

  useEffect(() => flush, [flush])

  const update = useCallback(
    (next, { debounce = 0 } = {}) => {
      if (readOnly) return
      const resolved = typeof next === 'function' ? next(current.current) : next
      current.current = resolved
      setLocal(resolved)
      pending.current = resolved
      if (debounce > 0) {
        clearTimeout(timer.current)
        timer.current = setTimeout(flush, debounce)
      } else {
        flush()
      }
    },
    [readOnly, flush],
  )

  return [value, update]
}
